import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Category labels for display
const CATEGORY_LABELS = {
  spot_load: '📦 Spot Load',
  precheck: '🔍 Pre-Check',
  damage: '⚠️ Damage',
  inspection: '🔧 Inspection',
  ro_upload: '📋 RO Upload',
  bluedriver_scan: '📊 BlueDriver Scan',
  inspection_video: '🎥 Video',
  upload: '📎 Upload',
};

// File type icons
const FILE_TYPE_ICONS = {
  image: '🖼️',
  pdf: '📄',
  video: '🎬',
  document: '📝',
};

/**
 * VehicleFilesModal - Master photo library for a vehicle
 * Shows all files organized by category with thumbnails and lightbox
 */
const VehicleFilesModal = ({ isOpen, onClose, vehicleId, roId, vehicle }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into filteredFiles for navigation
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [vehicleNotes, setVehicleNotes] = useState(null);

  useEffect(() => {
    if (isOpen && (vehicleId || roId)) {
      loadFiles();
      if (vehicleId && !roId) {
        loadVehicleNotes();
      }
    }
  }, [isOpen, vehicleId, roId]);

  const loadVehicleNotes = async () => {
    try {
      if (vehicle?.notes !== undefined) {
        // Notes already in vehicle prop
        setVehicleNotes(vehicle.notes);
      } else if (vehicleId) {
        // Fetch vehicle to get notes
        const vehicleData = await api.getVehicle(vehicleId);
        setVehicleNotes(vehicleData.notes || null);
      }
    } catch (err) {
      console.error('Failed to load vehicle notes:', err);
      setVehicleNotes(null);
    }
  };

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (roId) {
        data = await api.getROFiles(roId);
      } else {
        data = await api.getVehicleFiles(vehicleId);
      }
      setFiles(data || []);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError(err.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const vid = vehicleId || files[0]?.vehicleId;
      if (!vid) {
        alert('No vehicle ID available for sync');
        return;
      }
      const result = await api.syncVehicleFiles(vid);
      alert(`Synced ${result.synced} files. Total: ${result.totalFiles}`);
      loadFiles();
    } catch (err) {
      console.error('Sync failed:', err);
      alert('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleFileClick = (file, index) => {
    // Check if it's a PDF by fileType or file extension
    const isPdf = file.fileType === 'pdf' || 
                  file.fileUrl?.toLowerCase().endsWith('.pdf') ||
                  file.originalName?.toLowerCase().endsWith('.pdf');
    
    // Open all viewable files in lightbox (images, videos, PDFs)
    if (file.fileType === 'image' || file.fileType === 'video' || isPdf) {
      setLightboxIndex(index);
    } else {
      // Other documents open in new tab
      window.open(file.fileUrl, '_blank');
    }
  };

  const handleDeleteFile = async (e, file) => {
    e.stopPropagation(); // Prevent opening the file
    if (!window.confirm(`Delete this ${file.fileType || 'file'}? This cannot be undone.`)) return;
    
    setDeleting(file.id);
    try {
      await api.deleteVehicleFile(file.id);
      setFiles(prev => prev.filter(f => f.id !== file.id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete file: ' + err.message);
    } finally {
      setDeleting(null);
    }
  };

  // Helper to determine file type from MIME type
  const getFileType = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'document';
  };

  // Handle file uploads
  const handleFileUpload = async (uploadedFiles) => {
    if (!vehicleId || uploadedFiles.length === 0) return;
    
    setUploading(true);
    try {
      const uploadPromises = uploadedFiles.map(async (file) => {
        // Upload file to server
        const uploadResult = await api.uploadFile(file, 'vehicles');
        
        // Add to vehicle_files table
        const fileData = {
          vehicleId: vehicleId,
          fileUrl: uploadResult.url,
          fileType: getFileType(file.type),
          category: 'upload',
          originalName: file.name
        };
        
        return api.addVehicleFile(fileData);
      });
      
      await Promise.all(uploadPromises);
      
      // Refresh file list
      loadFiles();
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload files: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFileUpload(droppedFiles);
    }
  };

  // File input handler
  const handleFileInputChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      handleFileUpload(selectedFiles);
    }
  };

  // Get unique categories from files
  const categories = ['all', ...new Set(files.map(f => f.category).filter(Boolean))];

  // Filter files by selected category and sort: images first, then PDFs, then videos
  const fileTypeOrder = { image: 1, pdf: 2, video: 3, document: 4 };
  const filteredFiles = (selectedCategory === 'all' 
    ? files 
    : files.filter(f => f.category === selectedCategory)
  ).sort((a, b) => (fileTypeOrder[a.fileType] || 5) - (fileTypeOrder[b.fileType] || 5));

  // Navigation functions for lightbox
  const handlePrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(prev => prev > 0 ? prev - 1 : filteredFiles.length - 1);
  }, [lightboxIndex, filteredFiles.length]);
  
  const handleNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex(prev => prev < filteredFiles.length - 1 ? prev + 1 : 0);
  }, [lightboxIndex, filteredFiles.length]);
  
  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
      else if (e.key === 'Escape') setLightboxIndex(null);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, handlePrev, handleNext]);

  // Group files by category for display
  const groupedFiles = filteredFiles.reduce((acc, file) => {
    const cat = file.category || 'upload';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(file);
    return acc;
  }, {});

  if (!isOpen) return null;

  const vehicleInfo = vehicle 
    ? `${vehicle.stockNumber} - ${vehicle.year} ${vehicle.make} ${vehicle.model}`
    : files[0] 
      ? `${files[0].stockNumber} - ${files[0].year} ${files[0].make} ${files[0].model}`
      : 'Vehicle Files';

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200">
            <div>
              <h2 className="text-lg font-bold text-neutral-800">📎 Vehicle Files</h2>
              <p className="text-sm text-neutral-500">{vehicleInfo}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50"
              >
                {syncing ? '⏳ Syncing...' : '🔄 Sync'}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Vehicle Notes Section */}
          {vehicleNotes && (
            <div className="p-4 border-b border-neutral-200 bg-yellow-50">
              <div className="flex items-start gap-2">
                <span className="text-lg">📝</span>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-neutral-700 mb-1">Vehicle Notes</div>
                  <div className="text-sm text-neutral-800 whitespace-pre-wrap">{vehicleNotes}</div>
                </div>
              </div>
            </div>
          )}

          {/* Category Filter */}
          <div className="flex gap-2 p-3 border-b border-neutral-100 overflow-x-auto">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {cat === 'all' ? '📁 All' : CATEGORY_LABELS[cat] || cat}
                {cat !== 'all' && (
                  <span className="ml-1 opacity-70">
                    ({files.filter(f => f.category === cat).length})
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div 
            className={`flex-1 overflow-y-auto p-4 ${dragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-neutral-500">Loading files...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 mb-2">❌ {error}</p>
                <button onClick={loadFiles} className="text-blue-600 hover:underline">
                  Try again
                </button>
              </div>
            ) : uploading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-neutral-500">Uploading files...</span>
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-4xl mb-3">📭</p>
                <p className="text-neutral-500 mb-4">No files found for this vehicle</p>
                <div className="space-y-3">
                  <button
                    onClick={handleSync}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mr-2"
                  >
                    🔄 Sync Files from Records
                  </button>
                  <div className="text-neutral-400 text-sm">or</div>
                  <label className="inline-block px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer">
                    📎 Upload Files
                    <input
                      type="file"
                      multiple
                      onChange={handleFileInputChange}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                  <div className="text-xs text-neutral-400 mt-2">
                    Or drag and drop files anywhere in this window
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Upload Zone when files exist */}
                <div className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center bg-neutral-50 hover:bg-neutral-100 transition-colors">
                  <div className="text-2xl mb-2">📎</div>
                  <p className="text-sm text-neutral-600 mb-3">Drag and drop files here to upload</p>
                  <label className="inline-block px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 cursor-pointer">
                    Choose Files
                    <input
                      type="file"
                      multiple
                      onChange={handleFileInputChange}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
                    />
                  </label>
                </div>

                {Object.entries(groupedFiles).map(([category, categoryFiles]) => (
                  <div key={category}>
                    <h3 className="text-sm font-semibold text-neutral-700 mb-3">
                      {CATEGORY_LABELS[category] || category}
                      <span className="ml-2 text-neutral-400 font-normal">
                        ({categoryFiles.length})
                      </span>
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {categoryFiles.map(file => {
                        const fileIndex = filteredFiles.findIndex(f => f.id === file.id);
                        return (
                        <div
                          key={file.id}
                          onClick={() => handleFileClick(file, fileIndex)}
                          className="group relative bg-neutral-50 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow border border-neutral-200"
                        >
                          {/* Thumbnail */}
                          {file.fileType === 'image' ? (
                            <div className="aspect-square">
                              <img
                                src={file.fileUrl}
                                alt={file.originalName || 'File'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y="50" x="50" text-anchor="middle" font-size="40">🖼️</text></svg>';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="aspect-square flex items-center justify-center bg-neutral-100">
                              <span className="text-4xl">
                                {FILE_TYPE_ICONS[file.fileType] || '📎'}
                              </span>
                            </div>
                          )}
                          
                          {/* Overlay with info */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end">
                            <div className="w-full p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-white text-xs truncate">
                                {file.originalName || file.category || 'File'}
                              </p>
                              <p className="text-white/70 text-[10px]">
                                {new Date(file.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>

                          {/* File type badge */}
                          <div className="absolute top-2 right-2 bg-white/90 rounded px-1.5 py-0.5 text-[10px] font-medium">
                            {file.fileType?.toUpperCase() || 'FILE'}
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={(e) => handleDeleteFile(e, file)}
                            disabled={deleting === file.id}
                            className="absolute top-2 left-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600 disabled:opacity-50"
                            title="Delete file"
                          >
                            {deleting === file.id ? '...' : '×'}
                          </button>
                        </div>
                      )})}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-neutral-200 bg-neutral-50 text-center text-xs text-neutral-500">
            {files.length} file{files.length !== 1 ? 's' : ''} total
            {filteredFiles.length !== files.length && ` • ${filteredFiles.length} shown`}
          </div>
        </div>
      </div>

      {/* Lightbox for images, videos, and PDFs */}
      {lightboxIndex !== null && filteredFiles[lightboxIndex] && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60]"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-4 right-4 text-white text-2xl hover:text-neutral-300 z-10 w-10 h-10 flex items-center justify-center bg-black/50 rounded-full"
          >
            ✕
          </button>
          
          {/* Previous arrow */}
          {filteredFiles.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); handlePrev(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-neutral-300 z-10 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full"
            >
              ‹
            </button>
          )}
          
          {/* Next arrow */}
          {filteredFiles.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-4xl hover:text-neutral-300 z-10 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full"
            >
              ›
            </button>
          )}
          
          {/* File counter */}
          <div className="absolute top-4 left-4 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
            {lightboxIndex + 1} / {filteredFiles.length}
          </div>
          
          {/* Content */}
          <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {filteredFiles[lightboxIndex].fileType === 'image' ? (
              <img
                src={filteredFiles[lightboxIndex].fileUrl}
                alt={filteredFiles[lightboxIndex].originalName || 'Image'}
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : filteredFiles[lightboxIndex].fileType === 'video' ? (
              <video
                key={filteredFiles[lightboxIndex].id}
                src={filteredFiles[lightboxIndex].fileUrl}
                controls
                autoPlay
                className="max-w-full max-h-[90vh]"
              />
            ) : (filteredFiles[lightboxIndex].fileType === 'pdf' || 
                   filteredFiles[lightboxIndex].fileUrl?.toLowerCase().endsWith('.pdf') ||
                   filteredFiles[lightboxIndex].originalName?.toLowerCase().endsWith('.pdf')) ? (
              <iframe
                key={filteredFiles[lightboxIndex].id}
                src={filteredFiles[lightboxIndex].fileUrl}
                title={filteredFiles[lightboxIndex].originalName || 'PDF'}
                className="w-[80vw] h-[85vh] bg-white rounded"
              />
            ) : null}
          </div>
          
          {/* File name */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full max-w-[80vw] truncate">
            {filteredFiles[lightboxIndex].originalName || filteredFiles[lightboxIndex].category || 'File'}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * VehicleFilesButton - A button that opens the VehicleFilesModal
 * Shows file count badge when files exist
 */
export const VehicleFilesButton = ({ vehicleId, roId, vehicle, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fileCount, setFileCount] = useState(null);

  useEffect(() => {
    if (vehicleId) {
      api.getVehicleFileCount(vehicleId)
        .then(data => setFileCount(parseInt(data?.total) || 0))
        .catch(() => setFileCount(0));
    }
  }, [vehicleId]);

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
        className={`inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 rounded transition-colors ${className}`}
        title="View all files for this vehicle"
      >
        <span>📎</span>
        <span>Files</span>
        {fileCount > 0 && (
          <span className="bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
            {fileCount}
          </span>
        )}
      </button>

      <VehicleFilesModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        vehicleId={vehicleId}
        roId={roId}
        vehicle={vehicle}
      />
    </>
  );
};

export default VehicleFilesModal;
